/**
 * Namespaced localStorage with TTL. Keys are always scoped by restaurant
 * and (when relevant) element/table id — never global `cart` / `token`.
 */

const PREFIX = "opm";

/** Pre-auth browse / cart bucket (per restaurant). */
export const GUEST_ELEMENT_SCOPE = "_guest";

/**
 * The table scope the guest's OPM token belongs to, for keys and the opm_element_id cookie:
 * the table (element_id) when the token names one, else the open table session — a token from
 * the table code carries dining_session_id only. Null when the token names neither.
 */
export const tableScopeOf = (claims) => {
  if (claims?.element_id != null && claims.element_id !== "") return String(claims.element_id);
  if (claims?.dining_session_id != null && claims.dining_session_id !== "") {
    return `session-${claims.dining_session_id}`;
  }
  return null;
};

const DEFAULT_TTL_SECONDS = 4 * 60 * 60; // 4h, aligned with cookies

/** @param {string[]} parts */
function buildKey(parts, suffix) {
  return [PREFIX, ...parts, suffix].join(":");
}

function restaurantParts(restaurantId) {
  return ["restaurant", restaurantId];
}

function elementParts(restaurantId, elementId) {
  return ["restaurant", restaurantId, "element", elementId];
}

export const Keys = {
  /** Menu JSON cache — restaurant scope + TTL */
  menu: (restaurantId) => buildKey(restaurantParts(restaurantId), "menu"),
  /** @deprecated derived from menu at runtime; kept for one-shot migration */
  categoryList: (restaurantId) =>
    buildKey(restaurantParts(restaurantId), "categories"),
  orderList: (restaurantId, elementId) =>
    buildKey(elementParts(restaurantId, elementId), "orderList"),
  currentOrder: (restaurantId, elementId) =>
    buildKey(elementParts(restaurantId, elementId), "currentOrder"),
  selectedTab: (restaurantId) =>
    buildKey(restaurantParts(restaurantId), "selectedTab"),
  selectedItem: (restaurantId, elementId) =>
    buildKey(elementParts(restaurantId, elementId), "selectedItem"),
  /** JWT fallback when HttpOnly cookie is not available */
  token: (restaurantId, elementId) =>
    buildKey(elementParts(restaurantId, elementId), "token"),
  /** Display name for checkout — element scope */
  guestName: (restaurantId, elementId) =>
    buildKey(elementParts(restaurantId, elementId), "guestName"),
  /** Dining session from first (and subsequent) order create response — element scope */
  diningSessionId: (restaurantId, elementId) =>
    buildKey(elementParts(restaurantId, elementId), "diningSessionId"),
  /** Dishes the guest marked with a heart — kept on this phone only */
  favorites: (restaurantId) =>
    buildKey(restaurantParts(restaurantId), "favorites"),
  /** The web payment the guest left for the acquirer's page with — element scope, short TTL */
  webPayment: (restaurantId, elementId) =>
    buildKey(elementParts(restaurantId, elementId), "webPayment"),
};

const UI_LANGUAGE_KEY = `${PREFIX}:ui:language`;

export const UiKeys = {
  language: () => UI_LANGUAGE_KEY,
};

function now() {
  return Date.now();
}

export class OpmScopedStorage {
  /**
   * @param {Storage} [driver]
   */
  constructor(driver = localStorage) {
    this.driver = driver;
    /** Set once the guest's session ended: nothing is saved again until the page reloads. */
    this.writesDisabled = false;
  }

  disableWrites() {
    this.writesDisabled = true;
  }

  /**
   * @template T
   * @param {string} key
   * @param {T} value
   * @param {number} [ttlSeconds]
   */
  setWithTtl(key, value, ttlSeconds) {
    if (this.writesDisabled) return;
    const wrapped = {
      value,
      expiresAt:
        ttlSeconds != null && ttlSeconds > 0
          ? now() + ttlSeconds * 1000
          : undefined,
    };
    try {
      this.driver.setItem(key, JSON.stringify(wrapped));
    } catch {
      /* quota / private mode */
    }
  }

  /**
   * @template T
   * @param {string} key
   * @returns {T | null}
   */
  getWithTtl(key) {
    let raw;
    try {
      raw = this.driver.getItem(key);
    } catch {
      return null;
    }
    if (raw == null || raw === "") return null;
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return null;
    }
    if (!parsed || typeof parsed !== "object" || !("value" in parsed)) {
      return null;
    }
    if (parsed.expiresAt != null && parsed.expiresAt <= now()) {
      try {
        this.driver.removeItem(key);
      } catch {
        /* ignore */
      }
      return null;
    }
    return parsed.value;
  }

  /** @param {string} key */
  remove(key) {
    try {
      this.driver.removeItem(key);
    } catch {
      /* ignore */
    }
  }

  /**
   * Remove all keys for one restaurant (prefix match).
   * @param {string} restaurantId
   */
  clearRestaurant(restaurantId) {
    const prefix = `${PREFIX}:restaurant:${restaurantId}:`;
    this.removeKeysStartingWith(prefix);
  }

  /**
   * Remove keys for one table/session under a restaurant.
   * @param {string} restaurantId
   * @param {string} elementId
   */
  clearElement(restaurantId, elementId) {
    const prefix = `${PREFIX}:restaurant:${restaurantId}:element:${elementId}:`;
    this.removeKeysStartingWith(prefix);
  }

  /** Remove every key OPM saved (all `opm:*` entries, any restaurant, plus the UI language). */
  clearAll() {
    this.removeKeysStartingWith(`${PREFIX}:`);
  }

  /** @returns {number} number of entries removed */
  cleanupExpired() {
    let removed = 0;
    try {
      for (let i = 0; i < this.driver.length; i++) {
        const key = this.driver.key(i);
        if (!key || !key.startsWith(`${PREFIX}:`)) continue;
        const raw = this.driver.getItem(key);
        let parsed;
        try {
          parsed = raw ? JSON.parse(raw) : null;
        } catch {
          continue;
        }
        if (
          parsed &&
          parsed.expiresAt != null &&
          parsed.expiresAt <= now()
        ) {
          this.driver.removeItem(key);
          removed++;
        }
      }
    } catch {
      /* ignore */
    }
    return removed;
  }

  removeKeysStartingWith(prefix) {
    const toDelete = [];
    try {
      for (let i = 0; i < this.driver.length; i++) {
        const key = this.driver.key(i);
        if (key && key.startsWith(prefix)) toDelete.push(key);
      }
    } catch {
      return;
    }
    toDelete.forEach((k) => this.remove(k));
  }
}

export const opmStorage = new OpmScopedStorage();

export { DEFAULT_TTL_SECONDS };

/**
 * Copy order data from guest scope into authenticated element scope, then clear guest keys.
 * @param {string} restaurantId
 * @param {string} elementId
 */
/** Removes the stored cart (orderList and currentOrder) of each given element scope. */
export function clearStoredCart(restaurantId, elementIds) {
  for (const elementId of elementIds) {
    opmStorage.remove(Keys.orderList(restaurantId, elementId));
    opmStorage.remove(Keys.currentOrder(restaurantId, elementId));
  }
}

export function migrateGuestCartToElement(restaurantId, elementId) {
  const g = GUEST_ELEMENT_SCOPE;
  for (const suffix of ["orderList", "currentOrder"]) {
    const fromKey =
      suffix === "orderList"
        ? Keys.orderList(restaurantId, g)
        : Keys.currentOrder(restaurantId, g);
    const toKey =
      suffix === "orderList"
        ? Keys.orderList(restaurantId, elementId)
        : Keys.currentOrder(restaurantId, elementId);
    const data = opmStorage.getWithTtl(fromKey);
    if (!data) continue;
    const existing = opmStorage.getWithTtl(toKey);
    const merged =
      existing &&
      Array.isArray(existing.items) &&
      existing.items.length > 0 &&
      Array.isArray(data.items) &&
      data.items.length > 0
        ? data
        : existing?.items?.length
          ? existing
          : data;
    opmStorage.setWithTtl(toKey, merged, DEFAULT_TTL_SECONDS);
    opmStorage.remove(fromKey);
  }

  const guestDs = opmStorage.getWithTtl(Keys.diningSessionId(restaurantId, g));
  if (guestDs != null) {
    opmStorage.setWithTtl(
      Keys.diningSessionId(restaurantId, elementId),
      guestDs,
      DEFAULT_TTL_SECONDS
    );
    opmStorage.remove(Keys.diningSessionId(restaurantId, g));
  }
}

/** One-time removal of flat keys from before namespaced storage. */
export function removeLegacyGlobalKeys() {
  const legacy = [
    "menu",
    "orderList",
    "currentOrder",
    "categoryList",
    "selectedTab",
    "selectedItem",
    "language",
    "opmToken",
  ];
  legacy.forEach((k) => {
    try {
      localStorage.removeItem(k);
    } catch {
      /* ignore */
    }
  });
}
